import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Input, Modal, Select, Space, Table, Tabs, Tag, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import request from '../../api/request';
import './index.css';
import CustomerServiceConfigPanel, { CUSTOMER_SERVICE_CATEGORY } from './components/CustomerServiceConfigPanel';

const { TextArea } = Input;

interface Article {
  id: number;
  title: string;
  content: string;
  category: string;
  author: string;
  status: string;
  coverImage?: string | null;
  views: number;
  createdAt: string;
  updatedAt: string;
}

interface Faq {
  id: number;
  question: string;
  answer: string;
  category: string;
  sort: number;
  status: string;
  createdAt: string;
}

const articleCategories = [
  { label: '公告', value: 'notice' },
  { label: '指南', value: 'guide' },
  { label: '新闻', value: 'news' },
  { label: '用户协议', value: 'user_agreement' },
  { label: '隐私政策', value: 'privacy_policy' },
  { label: '客服信息', value: 'customer_service' },
  { label: '首页公告', value: 'home_notice' },
];

const faqCategories = [
  { label: '通用', value: 'general' },
  { label: '产品', value: 'product' },
  { label: '服务', value: 'service' },
];

const categoryLabel = (value: string) =>
  [...articleCategories, ...faqCategories].find((item) => item.value === value)?.label || value;

const ContentPage: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [articleModalVisible, setArticleModalVisible] = useState(false);
  const [faqModalVisible, setFaqModalVisible] = useState(false);
  const [currentArticle, setCurrentArticle] = useState<Article | null>(null);
  const [currentFaq, setCurrentFaq] = useState<Faq | null>(null);
  const [articleForm] = Form.useForm();
  const [faqForm] = Form.useForm();
  const customerServiceArticle = articles.find((article) => article.category === CUSTOMER_SERVICE_CATEGORY) || null;

  const fetchContent = async () => {
    setLoading(true);
    try {
      const [articleData, faqData] = await Promise.all([
        request({ url: '/api/content/articles', method: 'GET' }),
        request({ url: '/api/content/faqs', method: 'GET' }),
      ]);
      setArticles((articleData as unknown as Article[]) || []);
      setFaqs((faqData as unknown as Faq[]) || []);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '获取内容失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const filteredArticles = articles.filter((article) =>
    [article.title, categoryLabel(article.category), article.status]
      .join(' ')
      .toLowerCase()
      .includes(searchText.toLowerCase()),
  );

  const filteredFaqs = faqs.filter((faq) =>
    [faq.question, categoryLabel(faq.category), faq.status]
      .join(' ')
      .toLowerCase()
      .includes(searchText.toLowerCase()),
  );

  const showArticleModal = (article?: Article) => {
    setCurrentArticle(article || null);
    articleForm.setFieldsValue(article || {
      author: '管理员',
      category: 'notice',
      status: 'published',
      content: '',
    });
    setArticleModalVisible(true);
  };

  const showFaqModal = (faq?: Faq) => {
    setCurrentFaq(faq || null);
    faqForm.setFieldsValue(faq || {
      category: 'general',
      status: 'active',
      sort: faqs.length + 1,
    });
    setFaqModalVisible(true);
  };

  const saveArticle = async () => {
    const values = await articleForm.validateFields();
    if (currentArticle) {
      await request({ url: `/api/content/articles/${currentArticle.id}`, method: 'PATCH', data: values });
      message.success('文章已更新');
    } else {
      await request({ url: '/api/content/articles', method: 'POST', data: values });
      message.success('文章已创建');
    }
    setArticleModalVisible(false);
    fetchContent();
  };

  const saveFaq = async () => {
    const values = await faqForm.validateFields();
    if (currentFaq) {
      await request({ url: `/api/content/faqs/${currentFaq.id}`, method: 'PATCH', data: values });
      message.success('FAQ 已更新');
    } else {
      await request({ url: '/api/content/faqs', method: 'POST', data: values });
      message.success('FAQ 已创建');
    }
    setFaqModalVisible(false);
    fetchContent();
  };

  const deleteArticle = (id: number) => {
    Modal.confirm({
      title: '确认删除文章',
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        await request({ url: `/api/content/articles/${id}`, method: 'DELETE' });
        message.success('文章已删除');
        fetchContent();
      },
    });
  };

  const deleteFaq = (id: number) => {
    Modal.confirm({
      title: '确认删除 FAQ',
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        await request({ url: `/api/content/faqs/${id}`, method: 'DELETE' });
        message.success('FAQ 已删除');
        fetchContent();
      },
    });
  };

  const articleColumns: ColumnsType<Article> = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '标题', dataIndex: 'title', ellipsis: true },
    { title: '分类', dataIndex: 'category', width: 120, render: (value) => <Tag>{categoryLabel(value)}</Tag> },
    { title: '状态', dataIndex: 'status', width: 100, render: (value) => <Tag color={value === 'published' ? 'green' : 'gold'}>{value}</Tag> },
    { title: '浏览', dataIndex: 'views', width: 80 },
    {
      title: '操作',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => showArticleModal(record)}>编辑</Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteArticle(record.id)}>删除</Button>
        </Space>
      ),
    },
  ];

  const faqColumns: ColumnsType<Faq> = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '问题', dataIndex: 'question', ellipsis: true },
    { title: '分类', dataIndex: 'category', width: 120, render: (value) => <Tag>{categoryLabel(value)}</Tag> },
    { title: '排序', dataIndex: 'sort', width: 80 },
    { title: '状态', dataIndex: 'status', width: 100, render: (value) => <Tag color={value === 'active' ? 'green' : 'gold'}>{value}</Tag> },
    {
      title: '操作',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => showFaqModal(record)}>编辑</Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteFaq(record.id)}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="content-container">
      <Card
        title="内容管理"
        extra={
          <Input
            placeholder="搜索标题、分类、状态"
            prefix={<SearchOutlined />}
            allowClear
            style={{ width: 240 }}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        }
      >
        <Tabs
          items={[
            {
              key: 'articles',
              label: '文章 / 协议 / 客服',
              children: (
                <>
                  <div className="content-actions">
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => showArticleModal()}>
                      新增内容
                    </Button>
                  </div>
                  <Table
                    rowKey="id"
                    loading={loading}
                    columns={articleColumns}
                    dataSource={filteredArticles}
                    size="small"
                  />
                </>
              ),
            },
            {
              key: 'faqs',
              label: '常见问题',
              children: (
                <>
                  <div className="content-actions">
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => showFaqModal()}>
                      新增 FAQ
                    </Button>
                  </div>
                  <Table rowKey="id" loading={loading} columns={faqColumns} dataSource={filteredFaqs} size="small" />
                </>
              ),
            },
            {
              key: 'customer-service',
              label: '客服配置',
              children: <CustomerServiceConfigPanel article={customerServiceArticle} onSaved={fetchContent} />,
            },
          ]}
        />
      </Card>

      <Modal
        title={currentArticle ? '编辑内容' : '新增内容'}
        open={articleModalVisible}
        onCancel={() => setArticleModalVisible(false)}
        onOk={saveArticle}
        width={760}
        destroyOnClose
      >
        <Form form={articleForm} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="category" label="分类" rules={[{ required: true, message: '请选择分类' }]}>
            <Select options={articleCategories} />
          </Form.Item>
          <Form.Item name="author" label="作者" rules={[{ required: true, message: '请输入作者' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
            <Select
              options={[
                { label: '已发布', value: 'published' },
                { label: '草稿', value: 'draft' },
                { label: '归档', value: 'archived' },
              ]}
            />
          </Form.Item>
          <Form.Item name="content" label="内容" rules={[{ required: true, message: '请输入内容' }]}>
            <TextArea rows={10} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={currentFaq ? '编辑 FAQ' : '新增 FAQ'}
        open={faqModalVisible}
        onCancel={() => setFaqModalVisible(false)}
        onOk={saveFaq}
        width={680}
        destroyOnClose
      >
        <Form form={faqForm} layout="vertical">
          <Form.Item name="question" label="问题" rules={[{ required: true, message: '请输入问题' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="category" label="分类" rules={[{ required: true, message: '请选择分类' }]}>
            <Select options={faqCategories} />
          </Form.Item>
          <Form.Item name="sort" label="排序">
            <Input type="number" />
          </Form.Item>
          <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
            <Select
              options={[
                { label: '启用', value: 'active' },
                { label: '停用', value: 'inactive' },
              ]}
            />
          </Form.Item>
          <Form.Item name="answer" label="回答" rules={[{ required: true, message: '请输入回答' }]}>
            <TextArea rows={8} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ContentPage;
